import React, { useState, useEffect } from 'react';
import { Search, X, FileText, Loader2, Download, FileArchive, Image as ImgIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { uploadFile } from '../../hooks/useApi';
import { toast } from 'sonner';

export const EMOJI_GROUPS = [
  ['Smileys', ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😜', '🤪', '🤔', '🤨', '😐', '🙄', '😏', '😴', '😌', '🙃', '🤑', '😲', '😖', '😞', '😤', '😢', '😭', '😨', '😩', '🤯', '😬', '😱', '🥵', '🥶', '😳', '🤢', '🤮', '😷', '🤠', '😎', '🤓', '🧐', '🥳', '😈', '🤡']],
  ['Gestures', ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '👈', '👉', '👆', '👇', '✋', '🖖', '👋', '🤙', '💪', '🙏', '👏', '🙌', '🤝', '🫡', '🤷', '🤦']],
  ['Hearts', ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '❣️', '💕', '💞', '💖', '💘', '💝']],
  ['Fun', ['🔥', '💯', '⚡', '✨', '🌟', '💫', '💥', '🎉', '🎊', '🎁', '🏆', '🥇', '🎯', '🚀', '💰', '💎', '🪙', '📈', '📉', '🧠', '👀', '💀', '☠️', '🤖', '👾', '🎮', '🕹️', '💻', '📱', '🍕', '☕', '🍿', '⭐', '🌙', '🎵']],
];

const RECENT_KEY = 'nuyed_recent_emojis';
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
const pushRecent = (e) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify([e, ...getRecent().filter((x) => x !== e)].slice(0, 16))); } catch {} };

export function EmojiPicker({ onPick, className = '' }) {
  const [q, setQ] = useState('');
  const recent = getRecent();
  const pick = (e) => { pushRecent(e); onPick(e); };
  return (
    <div className={`gx-picker w-72 max-h-72 flex flex-col overflow-hidden ${className}`} data-testid="emoji-picker">
      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/[0.06]">
        <Search className="w-3.5 h-3.5 text-gx-muted flex-shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups…" className="flex-1 bg-transparent text-xs focus:outline-none" data-testid="emoji-search" />
      </div>
      <div className="overflow-y-auto p-2 flex-1">
        {!q && recent.length > 0 && (
          <div>
            <p className="text-[9px] uppercase tracking-widest text-gx-muted font-display px-1 pt-1 pb-0.5">Recent</p>
            <div className="flex flex-wrap">{recent.map((e) => <button key={e} type="button" onClick={() => pick(e)} className="text-lg p-1 hover:scale-125 transition" data-testid={`emoji-recent-${e}`}>{e}</button>)}</div>
          </div>
        )}
        {EMOJI_GROUPS.filter(([label]) => !q || label.toLowerCase().includes(q.toLowerCase())).map(([label, list]) => (
          <div key={label}>
            <p className="text-[9px] uppercase tracking-widest text-gx-muted font-display px-1 pt-1.5 pb-0.5">{label}</p>
            <div className="flex flex-wrap">
              {list.map((e) => (
                <button key={e} type="button" onClick={() => pick(e)} className="text-lg p-1 hover:scale-125 transition" data-testid={`emoji-${e}`}>{e}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function GifPicker({ onPick, onClose, className = '' }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/gifs/search', { params: { q } })
        .then((r) => { setItems(r.data.items || []); setNote(r.data.detail || (r.data.items?.length ? '' : 'No GIFs found')); })
        .catch((e) => { setItems([]); setNote(e?.response?.data?.detail || 'GIF search unavailable'); })
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className={`gx-picker w-72 overflow-hidden ${className}`} data-testid="gif-picker">
      <div className="flex items-center gap-2 p-2 border-b border-white/[0.08]">
        <Search className="w-3.5 h-3.5 text-gx-muted flex-shrink-0" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search GIFs…" className="flex-1 bg-transparent text-xs focus:outline-none" data-testid="gif-search-input" />
        <button type="button" onClick={onClose} className="p-1 text-gx-muted hover:text-gx-text" data-testid="gif-picker-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="h-64 overflow-y-auto p-2">
        {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gx-cyan" /></div>}
        {!loading && note && !items.length && <p className="text-[11px] text-gx-muted text-center px-4 py-8">{note}</p>}
        <div className="grid grid-cols-2 gap-1.5">
          {items.map((g) => (
            <button key={g.id} type="button" onClick={() => onPick(g)} className="rounded-lg overflow-hidden border border-white/[0.06] hover:border-gx-cyan/50 transition" data-testid={`gif-item-${g.id}`}>
              <img src={g.preview || g.url} alt={g.title} className="w-full h-24 object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>
      <p className="text-[9px] text-gx-muted text-center py-1 border-t border-white/[0.06]">Powered by GIPHY / Tenor</p>
    </div>
  );
}

const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'zip', 'rar', '7z', 'txt', 'csv', 'json', 'mp4', 'webm', 'mp3', 'doc', 'docx', 'xls', 'xlsx'];
export const ATTACH_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.zip,.rar,.7z,.txt,.csv,.json,.mp4,.webm,.mp3,.doc,.docx,.xls,.xlsx';
export const ATTACH_MAX_MB = 25;

export async function uploadAttachments(fileList, max = 4, onProgress) {
  const files = Array.from(fileList || []).slice(0, Math.max(0, max));
  const out = [];
  for (const f of files) {
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    const isPastedImage = !ext && (f.type || '').startsWith('image/');
    if (!ALLOWED_EXT.includes(ext) && !isPastedImage) { toast.error(`.${ext} not allowed (images, video, pdf, zip, docs…)`); continue; }
    if (f.size > ATTACH_MAX_MB * 1024 * 1024) { toast.error(`${f.name}: max ${ATTACH_MAX_MB}MB per file`); continue; }
    try {
      const up = await uploadFile(f, onProgress);
      out.push({ url: up.url, filename: f.name || `image.${(f.type || 'image/png').split('/')[1]}`, content_type: f.type || '', size: f.size });
    } catch { toast.error(`Upload failed: ${f.name}`); }
  }
  return out;
}

export function filesFromClipboard(e) {
  const items = Array.from(e.clipboardData?.items || []);
  return items.filter((it) => it.kind === 'file').map((it) => {
    const f = it.getAsFile();
    if (f && !f.name?.includes('.')) return new File([f], `pasted-${Date.now()}.${(f.type || 'image/png').split('/')[1] || 'png'}`, { type: f.type });
    return f;
  }).filter(Boolean);
}

export const isImg = (a) => (a?.content_type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp)(\?|$)/i.test(a?.filename || a?.url || '');
export const isVideo = (a) => (a?.content_type || '').startsWith('video/') || /\.(mp4|webm)(\?|$)/i.test(a?.filename || a?.url || '');
const fmtSize = (n) => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fileIcon = (a) => (/\.(zip|rar|7z)(\?|$)/i.test(a.filename || '') ? FileArchive : isImg(a) ? ImgIcon : FileText);

export function PendingAttachments({ atts, onRemove, uploading }) {
  if (!atts?.length && !uploading) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2" data-testid="pending-attachments">
      {(atts || []).map((a, i) => (
        <span key={i} className="flex items-center gap-1.5 pl-1.5 pr-1 py-1 rounded-lg bg-white/[0.04] border border-white/[0.1] text-[11px]">
          {isImg(a) ? <img src={a.url} className="w-7 h-7 rounded object-cover" alt="" /> : <FileText className="w-3.5 h-3.5 text-gx-cyan" />}
          <span className="max-w-[120px] truncate">{a.filename}</span>
          <button type="button" onClick={() => onRemove(i)} className="p-0.5 text-gx-muted hover:text-red-400" data-testid={`pending-attachment-remove-${i}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      {uploading && <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.1] text-[11px] text-gx-muted" data-testid="pending-uploading"><Loader2 className="w-3.5 h-3.5 animate-spin text-gx-cyan" /> Uploading…</span>}
    </div>
  );
}

export function Lightbox({ src, name, onClose }) {
  useEffect(() => {
    const k = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="gx-lightbox" onClick={onClose} data-testid="chat-lightbox">
      <button type="button" className="gx-lightbox-close" onClick={onClose} aria-label="Close" data-testid="chat-lightbox-close"><X className="w-5 h-5" /></button>
      <img src={src} alt={name || ''} onClick={(e) => e.stopPropagation()} />
      <a href={src} download={name || true} target="_blank" rel="noreferrer" className="gx-lightbox-dl" onClick={(e) => e.stopPropagation()}><Download className="w-3.5 h-3.5" /> {name || 'Open original'}</a>
    </div>
  );
}

export function AttachmentList({ atts, onOpen }) {
  if (!atts?.length) return null;
  return (
    <div className="gx-dm-media" data-testid="attachment-list">
      {atts.map((a, i) => {
        if (isImg(a)) return <img key={i} src={a.url} alt={a.filename} loading="lazy" onClick={() => onOpen?.(a)} data-testid={`attachment-img-${i}`} />;
        if (isVideo(a)) return <video key={i} src={a.url} controls preload="metadata" className="max-h-56 rounded-xl border border-white/[0.08]" data-testid={`attachment-video-${i}`} />;
        const Ic = fileIcon(a);
        return (
          <a key={i} href={a.url} target="_blank" rel="noreferrer" className="gx-dm-file" data-testid={`attachment-file-${i}`}>
            <Ic className="w-4 h-4" />
            <span className="min-w-0"><b>{a.filename || 'file'}</b><span>{fmtSize(a.size) || (a.filename || '').split('.').pop()?.toUpperCase() || 'File'}</span></span>
            <Download className="w-3.5 h-3.5 ml-auto opacity-70" />
          </a>
        );
      })}
    </div>
  );
}

export function MessageMedia({ m, onOpen }) {
  return (
    <>
      {m.gif_url && <img src={m.gif_url} className="max-h-52 rounded-xl mt-1.5 border border-white/[0.06] cursor-zoom-in" alt="GIF" loading="lazy" onClick={() => onOpen?.({ url: m.gif_url, filename: 'GIF' })} data-testid={`msg-gif-${m.id}`} />}
      <AttachmentList atts={m.attachments} onOpen={onOpen} />
    </>
  );
}

const TOKEN_RX = /(https?:\/\/[^\s<]+|@[a-zA-Z0-9_.-]{2,32})/g;
export function RichText({ text, className = '' }) {
  if (!text) return null;
  const parts = text.split(TOKEN_RX);
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (!p) return null;
        if (/^https?:\/\//.test(p)) return <a key={i} href={p} target="_blank" rel="noreferrer nofollow" className="gx-chat-link">{p.length > 60 ? p.slice(0, 57) + '…' : p}</a>;
        if (/^@[a-zA-Z0-9_.-]{2,32}$/.test(p)) return <Link key={i} to={`/user/${p.slice(1)}`} className="gx-chat-mention">{p}</Link>;
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </span>
  );
}
